import { observer } from "mobx-react-lite";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import Plot from "react-plotly.js";
import { Data, Layout, LayoutAxis } from "plotly.js";
import _ from "lodash";

import { PlotProps } from "./OverviewPlot";
import { videoPlayerService } from "../../../../services/video-player/VideoPlayerService";
import { secondsToMinutesAndSeconds } from "../../../../helpers/MathHelpers";
import { globalModule } from "../../../../services/service-provider/GlobalModule";
import { translationService } from "../../../../services/translation/TranslationService";
import {
  getProgressLine,
  getHoveredLine,
  getProgressOrHoveredAnnotation,
  getPlotDataForAddedDamages,
  getTimestamps,
  getLabel,
  getBackgroundForDetectedDamages,
  getLayoutConfiguration,
  getYaxis,
  getDetectedDamageBarsForMainCode,
  getBackgroundBar,
  getSeparatorLine,
} from "./PlotHelper";
import { PredictionGroup } from "../../../../services/inspection/VideoAnalysisService";
import { SecondaryCodeEnum } from "../../../../generated/Damages";

interface YAxisDomain {
  start: number;
  end: number;
}

export interface LaneForCodesAvailableForDetection {
  laneIndex: number;
  mainCode: string;
  secondaryCode: SecondaryCodeEnum;
  yAxisDomain: YAxisDomain;
}

export default observer(function CharacterizationsPlot(props: PlotProps) {
  const { videoAnalysisService, damagesStorage, onDoubleClick } = props;
  const [absoluteMousePosition, setAbsoluteMousePosition] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });
  const [relativeMousePosition, setRelativeMousePosition] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  useEffect(() => {
    const plotDiv = document.getElementById("characterizationPlot") as any;

    plotDiv?.addEventListener("click", (e: any) => {
      const xPos = plotDiv._fullLayout.xaxis.p2d(e.clientX - e.target.getBoundingClientRect().left);
      if (e.target.getAttribute("class")?.includes("nsewdrag drag"))
        videoPlayerService.jumpTo(_.clamp(xPos, 0, videoPlayerService.videoDuration));
    });

    return () => {
      plotDiv?.removeEventListener("click", () => {});
    };
  }, []);

  useEffect(() => {
    const plotDiv = document.getElementById("characterizationPlot") as any;

    plotDiv?.addEventListener("mousemove", (e: any) => {
      if (!e.target.getAttribute("class")?.includes("nsewdrag drag")) return;

      const relativeX = e.clientX - e.target.getBoundingClientRect().left;
      const relativeY = e.clientY - e.target.getBoundingClientRect().top;
      setRelativeMousePosition({ x: relativeX, y: relativeY });

      const xPos = plotDiv._fullLayout.xaxis.p2d(relativeX);
      const yPos = plotDiv._fullLayout.yaxis.p2d(relativeY);
      setAbsoluteMousePosition({ x: xPos, y: yPos });
    });
    plotDiv?.addEventListener("mouseleave", (e: any) => {
      const target = document.elementFromPoint(e.clientX, e.clientY);
      if (target?.getAttribute("class")?.includes("dragcover")) return;

      setRelativeMousePosition({ x: null, y: null });
      setAbsoluteMousePosition({ x: null, y: null });
    });

    return () => {
      plotDiv?.removeEventListener("mousemove", () => {});
      plotDiv?.removeEventListener("mouseleave", () => {});
    };
  }, []);

  const characterizationPredictionsGroupedByMainCodeAndSecondaryCode = useMemo(() => {
    const predictionGroups: PredictionGroup[] = [];

    for (const detectedMC of videoAnalysisService.detectedMainCodes.filter((x) => x.isDetected)) {
      for (const secondaryCode of detectedMC.secondaryCodes!) {
        predictionGroups.push({
          hasC1Code: secondaryCode.code === SecondaryCodeEnum.C1,
          hasC2Code: secondaryCode.code === SecondaryCodeEnum.C2,
          mainCode: detectedMC.code,
          predictions: [],
        });
      }
    }

    videoAnalysisService.buildPredictionGroupsForDetectedDamages(predictionGroups);

    return predictionGroups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoAnalysisService,
    videoAnalysisService.analysisConfidenceOption,
    videoAnalysisService.analysisOverallConfidence,
    videoAnalysisService.analysisIndividualConfidenceLevels,
  ]);

  const lanesForCodesAvailableForDetection =
    useCallback((): LaneForCodesAvailableForDetection[] => {
      let lanes = [];
      let currentLaneIndex = 0;

      for (const mainAndSecondaryCode of videoAnalysisService.codesAvailableForDetection
        .map((code) => code.mainCodesWithSecondaryCodes)
        .flat()) {
        const sorted = [...mainAndSecondaryCode.secondaryCodes].reverse();

        for (const secondaryCode of sorted) {
          lanes.push({
            laneIndex: currentLaneIndex,
            mainCode: mainAndSecondaryCode.mainCode,
            secondaryCode: secondaryCode.code,
            yAxisDomain: { start: 0, end: 0 },
          });
          currentLaneIndex++;
        }
      }

      lanes.forEach((lane) => {
        lane.yAxisDomain = {
          start: 0.07 + (lane.laneIndex * (1 - 0.06)) / lanes.length,
          end: 0.07 + ((lane.laneIndex + 1) * (1 - 0.06)) / lanes.length,
        };
      });

      return lanes;
    }, [videoAnalysisService.codesAvailableForDetection]);

  const getLanesForVisibleCodes = useCallback((): LaneForCodesAvailableForDetection[] => {
    const filteredLanes = lanesForCodesAvailableForDetection().filter((lane) => {
      const mainCode = videoAnalysisService.detectedMainCodes.find(
        (detectedMainCode) => detectedMainCode.code === lane.mainCode
      );

      const mainCodeDetected = characterizationPredictionsGroupedByMainCodeAndSecondaryCode.find(
        (item) => item.mainCode === mainCode?.code
      );

      return mainCode?.isVisible && mainCode?.isDetected && mainCodeDetected;
    });

    filteredLanes.forEach((lane, index) => {
      lane.laneIndex = index;
    });

    return filteredLanes;
  }, [
    characterizationPredictionsGroupedByMainCodeAndSecondaryCode,
    lanesForCodesAvailableForDetection,
    videoAnalysisService.detectedMainCodes,
  ]);

  const laneIndexForMainCode = useCallback(
    (mainCode: string, secondaryCode: SecondaryCodeEnum) => {
      const lanes = getLanesForVisibleCodes();

      return lanes.find(
        (lane) => lane.mainCode === mainCode && lane.secondaryCode === secondaryCode
      )?.laneIndex;
    },
    [getLanesForVisibleCodes]
  );

  const getSecondaryCodeForMainCode = useCallback((predictionGroup: PredictionGroup) => {
    return predictionGroup.hasC1Code
      ? SecondaryCodeEnum.C1
      : predictionGroup.hasC2Code
      ? SecondaryCodeEnum.C2
      : SecondaryCodeEnum.None;
  }, []);

  const createPlotData = useCallback(() => {
    let plotsData: Data[] = [];
    const characterizationGroups = characterizationPredictionsGroupedByMainCodeAndSecondaryCode;

    for (let index = 0; index < characterizationGroups.length; index++) {
      const group = characterizationGroups[index];
      const availableSecondaryCode = getSecondaryCodeForMainCode(group);
      let currentX: number[] = [];

      for (let i = 0; i < group.predictions.length; i++) {
        const prediction = group.predictions[i];

        if (prediction.timestamp <= videoPlayerService.videoDuration) {
          const hasNextPredictionSameCharacterization =
            videoAnalysisService.vaData.characterizationCodesWithColors[
              group.predictions[i].secondaryCode
            ] ===
            videoAnalysisService.vaData.characterizationCodesWithColors[
              group.predictions[i + 1]?.secondaryCode
            ];

          if (currentX.length === 0) {
            currentX.push(prediction.timestamp);
            if (hasNextPredictionSameCharacterization) continue;
          } else if (Math.abs(currentX[currentX.length - 1] - prediction.timestamp) < 1) {
            currentX.push(prediction.timestamp);

            if (i !== group.predictions.length - 1 && hasNextPredictionSameCharacterization)
              continue;
          }
        }

        const plotColor = videoAnalysisService.hasMainCodeOnly(group)
          ? "#B3B3B3"
          : videoAnalysisService.vaData.characterizationCodesWithColors[prediction.secondaryCode];

        const laneIndex = laneIndexForMainCode(group.mainCode, availableSecondaryCode);
        if (laneIndex !== undefined) {
          plotsData.push(
            getDetectedDamageBarsForMainCode(
              currentX,
              laneIndex,
              group,
              availableSecondaryCode.toString(),
              plotColor,
              i
            )
          );
        }
        currentX = [];
      }
    }

    plotsData.push(
      getPlotDataForAddedDamages(damagesStorage.damageDetailsForDiagram, videoAnalysisService, "y")
    );

    return plotsData;
  }, [
    characterizationPredictionsGroupedByMainCodeAndSecondaryCode,
    damagesStorage.damageDetailsForDiagram,
    getSecondaryCodeForMainCode,
    laneIndexForMainCode,
    videoAnalysisService,
  ]);

  const xAxisTimestampValues = useMemo(() => {
    return getTimestamps(videoPlayerService.videoDuration, 5, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlayerService.videoDuration]);

  const xaxis = useMemo<Partial<LayoutAxis>>(
    () => ({
      anchor: "y",
      showline: false,
      showticklabels: true,
      tickvals: xAxisTimestampValues,
      ticktext: xAxisTimestampValues.map((t) => secondsToMinutesAndSeconds(t)),
      range: [0, videoPlayerService.videoDuration],
      zeroline: false,
      showgrid: false,
    }),
    [xAxisTimestampValues]
  );

  const layout = useMemo(() => {
    const characterizationGroups = characterizationPredictionsGroupedByMainCodeAndSecondaryCode;

    const layout = {} as Layout;
    layout.autosize = true;
    setupLayout();

    plotLanesForDetectedVisibleDamages();
    plotBackgroundForEmptyLanes();

    layout.shapes.push(getBackgroundForDetectedDamages(videoPlayerService));
    layout.shapes.push(getProgressLine(videoPlayerService.videoProgress));
    const hoveredLine = getHoveredLine(videoPlayerService.videoDuration, absoluteMousePosition.x);
    if (hoveredLine) layout.shapes.push(hoveredLine);

    layout.annotations.push(
      ...getProgressOrHoveredAnnotation(
        videoPlayerService.videoProgress,
        videoPlayerService.videoDuration,
        videoAnalysisService,
        damagesStorage.damageDetailsForDiagram,
        absoluteMousePosition.x
      )
    );

    videoAnalysisService.plotRelayoutDone();

    return layout;

    function setupLayout() {
      layout.margin = { l: 23, r: 20, b: 28, t: 30, pad: 0 };
      layout.showlegend = false;
      layout.shapes = [];
      layout.annotations = [];
      layout.hoverdistance = 40;

      layout.xaxis = xaxis;

      layout.yaxis = {
        range: [0, 1],
        domain: [0, 0.06],
        showticklabels: false,
        showgrid: false,
        fixedrange: true,
      };
    }

    function plotLaneForDamage(
      laneIndex: number,
      index: number,
      colorCode: any,
      characterizationGroups: PredictionGroup[]
    ) {
      layout[`yaxis${laneIndex + 2}`] = getYaxis(laneIndex, lanesForCodesAvailableForDetection());
      layout.shapes.push(getBackgroundBar(videoPlayerService, laneIndex, colorCode, 0.4));
      layout.shapes.push(
        getSeparatorLine(
          videoPlayerService,
          laneIndex,
          lanesForCodesAvailableForDetection(),
          "below"
        )
      );
      layout.shapes.push(
        getSeparatorLine(
          videoPlayerService,
          laneIndex,
          lanesForCodesAvailableForDetection(),
          "above"
        )
      );

      layout.annotations.push(
        getLabel(
          videoAnalysisService,
          index,
          laneIndex,
          characterizationGroups,
          relativeMousePosition.x
        )
      );
    }

    function plotBackgroundForEmptyLanes() {
      const emptyLanes = lanesForCodesAvailableForDetection().filter(
        (lane) =>
          !getLanesForVisibleCodes()
            .map((visibleLane) => visibleLane.laneIndex)
            .includes(lane.laneIndex)
      );

      emptyLanes.forEach((lane, index) => {
        layout[`yaxis${lane.laneIndex + 2}`] = getYaxis(lane.laneIndex, emptyLanes);
        layout.shapes.push(getBackgroundBar(videoPlayerService, lane.laneIndex, "#f6f6f6"));

        if (index === 0) {
          layout.shapes.push(
            getSeparatorLine(
              videoPlayerService,
              lane.laneIndex,
              lanesForCodesAvailableForDetection(),
              "below"
            )
          );
        }
      });
    }

    function plotLanesForDetectedVisibleDamages() {
      for (let index = 0; index < characterizationGroups.length; index++) {
        const mainCode = characterizationGroups[index].mainCode;
        const colorCode = videoAnalysisService.mainCodeColors[mainCode];

        const laneIndex = laneIndexForMainCode(
          mainCode,
          getSecondaryCodeForMainCode(characterizationGroups[index])
        );

        if (laneIndex !== undefined) {
          plotLaneForDamage(laneIndex, index, colorCode, characterizationGroups);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoPlayerService.videoProgress,
    videoPlayerService.videoDuration,
    translationService.language,
    absoluteMousePosition,
    videoAnalysisService.plotNeedsRelayout,
    damagesStorage.isCollapsed,
    characterizationPredictionsGroupedByMainCodeAndSecondaryCode,
  ]);

  return (
    <Plot
      divId="characterizationPlot"
      data={createPlotData()}
      style={{
        width: "100%",
        height: "100%",
      }}
      layout={layout}
      config={getLayoutConfiguration()}
      onDoubleClick={() => onDoubleClick(absoluteMousePosition.x!)}
    />
  );
});
